import { tracker } from "@openreplay/tracker";
import { GetLoggedInUserQuery } from "types/graphql-schema";

type OpenReplayUser = Pick<
  NonNullable<GetLoggedInUserQuery["me"]>,
  "email" | "hasSyncedCalendar" | "id" | "name"
>;

type OpenReplayImpersonator = {
  id: number;
  name: string;
  email: string;
};

const OPENREPLAY_PROJECT_KEY = "Ou2klB0v1MoVWdjKwIte";
const OPENREPLAY_INGEST_POINT = "https://sessions.topicflow.com/ingest";

let configured = false;
let startPromise: Promise<void> | null = null;

function getUserID(user: OpenReplayUser) {
  return user.email || String(user.id);
}

function getMetadata(
  user: OpenReplayUser,
  impersonator?: OpenReplayImpersonator,
) {
  return {
    userId: String(user.id),
    displayName: user.name || "",
    email: user.email || "",
    hasSyncedCalendar: String(user.hasSyncedCalendar),
    ...(impersonator
      ? {
          isImpersonated: "true",
          impersonatorId: String(impersonator.id),
          impersonatorName: impersonator.name,
          impersonatorEmail: impersonator.email,
        }
      : {}),
  };
}

function ensureConfigured() {
  if (configured) {
    return;
  }
  tracker.configure({
    projectKey: OPENREPLAY_PROJECT_KEY,
    ingestPoint: OPENREPLAY_INGEST_POINT,
    obscureTextEmails: false,
    obscureInputEmails: false,
    defaultInputMode: 0,
    inlineCss: 3,
  });
  configured = true;
}

function syncUser(user: OpenReplayUser, impersonator?: OpenReplayImpersonator) {
  tracker.setUserID(getUserID(user));

  Object.entries(getMetadata(user, impersonator)).forEach(([key, value]) => {
    tracker.setMetadata(key, value);
  });
}

export function initializeOpenReplay(
  user: OpenReplayUser,
  impersonator?: OpenReplayImpersonator,
) {
  ensureConfigured();

  if (!startPromise) {
    startPromise = tracker
      .start({
        userID: getUserID(user),
        metadata: getMetadata(user, impersonator),
      })
      .then(() => undefined)
      .catch((error) => {
        startPromise = null;
        throw error;
      });
    return startPromise;
  }

  return startPromise.then(() => {
    syncUser(user, impersonator);
  });
}

/** The recording of this session at this moment, or undefined before it starts. */
export function getOpenReplaySessionUrl() {
  if (!startPromise) {
    return undefined;
  }
  try {
    return tracker.getSessionURL({ withCurrentTime: true });
  } catch {
    return undefined;
  }
}

export function resetOpenReplayForTests() {
  configured = false;
  startPromise = null;
}
